import { useState, useRef, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { Send, Mic, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWhisperInput } from '@/hooks/useWhisperInput';

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
}

function formatElapsed(seconds: number): string {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

export function ChatInput({ onSend, disabled }: Props) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasBlockedRef = useRef(false);
  const { state: speechState, liveTranscript, elapsed, start: startSpeech, stop: stopSpeech } = useWhisperInput();

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  // Resize whenever value changes (covers programmatic updates like speech input)
  useEffect(() => {
    resizeTextarea();
  }, [value]);

  // When the textarea transitions from disabled (streaming, listening, or transcribing) back to enabled,
  // re-focus it so the user can continue typing without clicking again.
  useEffect(() => {
    const blocked = !!disabled || speechState === 'listening' || speechState === 'transcribing';
    if (wasBlockedRef.current && !blocked) {
      textareaRef.current?.focus();
    }
    wasBlockedRef.current = blocked;
  }, [disabled, speechState]);

  function handleSend() {
    const text = value.trim();
    if (!text || disabled) return;
    onSend(text);
    setValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleMicClick() {
    if (speechState === 'listening') {
      stopSpeech();
      return;
    }
    if (speechState === 'transcribing') return;
    void startSpeech((transcript) => {
      setValue((prev) => (prev ? `${prev} ${transcript}` : transcript));
    });
  }

  const isListening = speechState === 'listening';
  const isTranscribing = speechState === 'transcribing';

  return (
    <div className="border-t border-border bg-background px-4 py-3">
      {isListening && (
        <div className="max-w-lg mx-auto mb-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
            </span>
            <span className="text-xs font-medium text-destructive">Aufnahme · {formatElapsed(elapsed)}</span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={stopSpeech}
              className="flex items-center gap-1.5 text-xs font-medium text-destructive hover:underline"
            >
              <Square size={12} fill="currentColor" />
              Stoppen
            </button>
          </div>
          {liveTranscript && (
            <p className="text-xs text-muted-foreground italic line-clamp-2">{liveTranscript}</p>
          )}
        </div>
      )}

      {isTranscribing && (
        <div className="max-w-lg mx-auto mb-2 rounded-xl border border-border bg-muted/50 px-3 py-2 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Transkribiere…</span>
        </div>
      )}

      <div className="flex items-end gap-2 max-w-lg mx-auto">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={resizeTextarea}
          placeholder={
            isListening
              ? 'Ich höre zu…'
              : isTranscribing
                ? 'Transkribiere…'
                : 'Schreib eine Nachricht…'
          }
          disabled={disabled || isListening || isTranscribing}
          rows={1}
          className="flex-1 resize-none bg-muted rounded-xl px-4 py-2.5 text-base leading-relaxed outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground disabled:opacity-50 max-h-40 overflow-y-auto"
        />

        <Button
          size="icon"
          variant={isListening ? 'destructive' : 'outline'}
          onClick={handleMicClick}
          disabled={disabled || isTranscribing || speechState === 'unsupported'}
          title={
            speechState === 'unsupported'
              ? 'Sprachaufnahme nicht unterstützt'
              : isListening
                ? 'Aufnahme stoppen'
                : isTranscribing
                  ? 'Transkribiere…'
                  : 'Spracheingabe starten'
          }
          className={`rounded-xl shrink-0 ${isListening ? 'animate-pulse' : ''}`}
          aria-label={isListening ? 'Aufnahme stoppen' : 'Spracheingabe'}
          type="button"
        >
          {isListening ? (
            <Square size={16} fill="currentColor" />
          ) : isTranscribing ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Mic size={18} />
          )}
        </Button>

        <Button
          size="icon"
          onClick={handleSend}
          disabled={!value.trim() || disabled || isListening || isTranscribing}
          className="rounded-xl shrink-0"
          aria-label="Senden"
          type="button"
        >
          <Send size={18} />
        </Button>
      </div>
    </div>
  );
}
